import { useEffect, useState } from "react";
import { Mic, MicOff, PhoneOff, MessageSquareText } from "lucide-react";
import { useVoiceLoop } from "@/lib/realtimeCall";
import { DAILY_LIMIT_MSG } from "@/lib/saathiClient";

const MAX_CALL_SEC = 10 * 60;

const ERRORS = {
  mic: "Saathi needs your microphone to talk. Please allow microphone access, or continue by typing.",
  unavailable: "Voice calls aren't available right now. You can keep talking to Saathi by typing.",
  daily_limit: DAILY_LIMIT_MSG,
};

const STATUS = {
  connecting: "Calling Saathi…",
  listening: "Go ahead, I'm listening",
  user: "Listening…",
  thinking: "Hmm, thinking…",
  saathi: "Saathi is speaking · talk to interrupt",
};

function Orb({ phase, connecting }) {
  const scale = phase === "saathi" ? "call-orb-speaking" : phase === "user" ? "call-orb-listening" : (connecting || phase === "thinking") ? "call-orb-connecting" : "call-orb-idle";
  return (
    <div className="relative w-56 h-56 sm:w-64 sm:h-64 flex items-center justify-center" data-testid="call-orb" data-phase={connecting ? "connecting" : phase}>
      <div className={`absolute inset-0 rounded-full bg-[#E07B39]/15 ${scale}`} />
      <div className={`absolute inset-6 rounded-full bg-[#E07B39]/25 ${scale}`} style={{ animationDelay: "120ms" }} />
      <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-[#F2A36B] to-[#E07B39] shadow-[0_20px_60px_-10px_rgba(224,123,57,0.7)] flex items-center justify-center text-5xl">🌸</div>
    </div>
  );
}

function useTimer(active) {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setSec((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
  return sec;
}

const CtrlBtn = ({ onClick, label, testid, danger, children }) => (
  <div className="flex flex-col items-center gap-2">
    <button onClick={onClick} aria-label={label} data-testid={testid}
      className={`w-16 h-16 rounded-full flex items-center justify-center transition-transform active:scale-95 ${danger ? "bg-[#D64545] hover:bg-[#c03a3a] text-white shadow-[0_10px_30px_-8px_rgba(214,69,69,0.6)]" : "bg-white/10 hover:bg-white/20 text-white border border-white/15"}`}>
      {children}
    </button>
    <span className="font-mono text-[10px] tracking-wider uppercase text-white/60">{label}</span>
  </div>
);

export function VoiceCall({ lang, orgSlug, history, conversationId, greeting, onDone }) {
  const call = useVoiceLoop();
  const sec = useTimer(call.status === "live");

  useEffect(() => {
    const t = setTimeout(() => call.start({ lang, orgSlug, history, conversationId, greeting }), 0);
    return () => { clearTimeout(t); call.end(); };
  }, []); // eslint-disable-line

  const hangUp = () => onDone(call.end());
  useEffect(() => { if (sec >= MAX_CALL_SEC) hangUp(); }, [sec]); // eslint-disable-line

  const last = call.turns.slice(-2);
  const statusText = call.status === "connecting" ? STATUS.connecting : STATUS[call.phase];
  const mmss = `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

  return (
    <div className="min-h-screen bg-[#1A1A2E] text-white flex flex-col relative overflow-hidden" dir={lang.rtl ? "rtl" : "ltr"} data-testid="voice-call-screen">
      <div className="absolute inset-0 saathi-texture opacity-[0.06]" />
      <div className="relative z-10 max-w-md mx-auto w-full flex-1 flex flex-col px-7 py-10">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-[28px] leading-none">Saathi</div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#E07B39] mt-1">{lang.sub} · voice call</div>
          </div>
          <div className="font-mono text-[13px] text-white/60" data-testid="call-timer">{call.status === "live" ? mmss : ""}</div>
        </div>

        {call.status === "error" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-6" data-testid="call-error">
            <div className="text-5xl">🌸</div>
            <p className="text-[16px] leading-relaxed text-white/85 max-w-[300px]" data-testid="call-error-message">{ERRORS[call.error] || ERRORS.unavailable}</p>
            <button onClick={hangUp} className="bg-[#E07B39] hover:bg-[#c96a2e] rounded-full px-7 py-4 text-[15px] font-medium" data-testid="call-continue-text-btn">Continue by typing →</button>
          </div>
        ) : (
          <>
            <div className="flex-1 flex flex-col items-center justify-center gap-8">
              <Orb phase={call.phase} connecting={call.status === "connecting"} />
              <div className="font-mono text-[12px] tracking-[0.2em] uppercase text-white/70" data-testid="call-status">{statusText}</div>
              <div className="min-h-[96px] w-full space-y-2 text-center" data-testid="call-captions">
                {last.map((t, i) => (
                  <p key={i} className={`text-[15px] leading-relaxed ${t.role === "user" ? "text-white/50" : "text-white/90"}`}>{t.content}</p>
                ))}
              </div>
            </div>
            <div className="flex items-end justify-center gap-8 pb-4">
              <CtrlBtn onClick={call.toggleMute} label={call.muted ? "unmute" : "mute"} testid="call-mute-btn">
                {call.muted ? <MicOff size={24} /> : <Mic size={24} />}
              </CtrlBtn>
              <CtrlBtn onClick={hangUp} label="end" testid="call-end-btn" danger><PhoneOff size={26} /></CtrlBtn>
              <CtrlBtn onClick={hangUp} label="type" testid="call-type-instead-btn"><MessageSquareText size={24} /></CtrlBtn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
